import * as React from 'react'
import { cn } from '@/lib/utils'

type DropdownContextValue = {
  open: boolean
  setOpen: (v: boolean) => void
  triggerId: string
  menuId: string
  /** Para devolver o foco a quem abriu quando o menu fecha. */
  triggerRef: React.RefObject<HTMLElement | null>
}

const DropdownContext = React.createContext<DropdownContextValue | null>(null)

function useDropdownContext() {
  const ctx = React.useContext(DropdownContext)
  if (!ctx) throw new Error('Dropdown subcomponents must be inside <Dropdown>')
  return ctx
}

export interface DropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ open, defaultOpen, onOpenChange, className, children, ...rest }, ref) => {
    const [internal, setInternal] = React.useState(defaultOpen ?? false)
    const current = open ?? internal
    const baseId = React.useId()
    const setOpen = React.useCallback(
      (v: boolean) => {
        if (open === undefined) setInternal(v)
        onOpenChange?.(v)
      },
      [open, onOpenChange]
    )

    const rootRef = React.useRef<HTMLDivElement>(null)
    const triggerRef = React.useRef<HTMLElement | null>(null)
    React.useImperativeHandle(ref, () => rootRef.current as HTMLDivElement)

    React.useEffect(() => {
      if (!current) return
      const handleClickOutside = (e: MouseEvent) => {
        if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
          setOpen(false)
        }
      }
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return
        setOpen(false)
        // Sem isto o menu desmonta e o foco cai no `<body>`: depois de
        // "Mover" ou "Arquivar" pela árvore, quem usa teclado voltava ao topo
        // da página.
        triggerRef.current?.focus?.()
      }
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }, [current, setOpen])

    return (
      <div
        ref={rootRef}
        className={cn('relative inline-block', className)}
        {...rest}
      >
        <DropdownContext.Provider
          value={{
            open: current,
            setOpen,
            triggerId: `${baseId}-trigger`,
            menuId: `${baseId}-menu`,
            triggerRef,
          }}
        >
          {children}
        </DropdownContext.Provider>
      </div>
    )
  }
)
Dropdown.displayName = 'Dropdown'

export interface DropdownTriggerProps {
  asChild?: boolean
  children: React.ReactElement
}

export const DropdownTrigger: React.FC<DropdownTriggerProps> = ({
  children,
}) => {
  const ctx = useDropdownContext()
  const childProps = children.props as Record<string, unknown>
  const onClick = childProps.onClick as
    ((e: React.MouseEvent) => void) | undefined

  const newProps: Record<string, unknown> = {
    ...childProps,
    // Guarda o gatilho para devolver o foco quando o menu fechar.
    ref: ctx.triggerRef,
    id: ctx.triggerId,
    'aria-haspopup': 'menu',
    'aria-expanded': ctx.open,
    'aria-controls': ctx.menuId,
    onClick: (e: React.MouseEvent) => {
      onClick?.(e)
      ctx.setOpen(!ctx.open)
    },
  }
  return React.cloneElement(
    children as React.ReactElement<Record<string, unknown>>,
    newProps
  )
}

export interface DropdownMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'end'
}

export const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  ({ className, align = 'end', children, ...rest }, ref) => {
    const ctx = useDropdownContext()
    const menuRef = React.useRef<HTMLDivElement>(null)
    React.useImperativeHandle(ref, () => menuRef.current as HTMLDivElement)

    /**
     * Ao abrir, o foco vai para o primeiro item.
     *
     * Sem isto o foco continua no GATILHO, que é irmão do menu: o `onKeyDown`
     * abaixo nunca recebia as setas, e "abrir o menu e apertar ↓" não fazia
     * nada — exatamente o que o contrato de `role="menu"` promete que funciona.
     */
    React.useEffect(() => {
      if (!ctx.open) return
      menuRef.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus()
    }, [ctx.open])

    if (!ctx.open) return null
    return (
      <div
        ref={menuRef}
        id={ctx.menuId}
        role="menu"
        aria-labelledby={ctx.triggerId}
        className={cn(
          'absolute z-50 mt-2 min-w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-md focus:outline-none dark:border-gray-700 dark:bg-gray-800',
          align === 'end' ? 'right-0' : 'left-0',
          className
        )}
        // `role="menu"` promete navegação por setas ao leitor de tela; sem
        // isto ele entra em modo aplicação e as setas não fazem nada.
        onKeyDown={event => {
          const itens = [
            ...event.currentTarget.querySelectorAll<HTMLElement>(
              '[role^="menuitem"]'
            ),
          ].filter(el => el.offsetParent !== null)
          if (itens.length === 0) return
          // `-1` (nada focado) + ArrowUp levaria ao PENÚLTIMO item; com o
          // ancoramento abaixo, sobe para o último, que é o esperado.
          const foco = itens.indexOf(document.activeElement as HTMLElement)
          const atual = foco === -1 ? (event.key === 'ArrowUp' ? 0 : -1) : foco
          const irPara = (i: number) => {
            event.preventDefault()
            itens[(i + itens.length) % itens.length]?.focus()
          }
          if (event.key === 'ArrowDown') irPara(atual + 1)
          else if (event.key === 'ArrowUp') irPara(atual - 1)
          else if (event.key === 'Home') irPara(0)
          else if (event.key === 'End') irPara(itens.length - 1)
        }}
        {...rest}
      >
        {children}
      </div>
    )
  }
)
DropdownMenu.displayName = 'DropdownMenu'

export interface DropdownItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  destructive?: boolean
}

export const DropdownItem = React.forwardRef<
  HTMLButtonElement,
  DropdownItemProps
>(({ className, destructive, children, onClick, ...rest }, ref) => {
  const ctx = useDropdownContext()
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed dark:focus-visible:bg-gray-700',
        destructive
          ? 'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20'
          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white',
        className
      )}
      onClick={e => {
        onClick?.(e)
        ctx.setOpen(false)
        ctx.triggerRef.current?.focus?.()
      }}
      {...rest}
    >
      {children}
    </button>
  )
})
DropdownItem.displayName = 'DropdownItem'

export const DropdownSeparator: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ className, ...rest }) => (
  <div
    role="separator"
    className={cn('my-1 h-px bg-gray-200 dark:bg-gray-700', className)}
    {...rest}
  />
)
